import { useCallback } from 'react';

import { useApolloClient } from '@apollo/client';

import { IdTypes } from 'bundles/product-features';
import { productFeaturesQuery } from 'bundles/product-features/private/queries';
import type { ProductFeaturesQueryData, ProductFeaturesQueryVariables } from 'bundles/product-features/private/types';

const NAMESPACE = 'enterprise';
const FEATURE_NAME = 'disableDomainValidationEmailPrefill';

/**
 * Returns a fetcher for the org-scoped `enterprise.disableDomainValidationEmailPrefill`
 * product feature. It is fetched imperatively because the third-party organization id
 * is only known asynchronously (from the SSO magic-link token response). Resolves to
 * `false` when no org id is available.
 */
const useDisableDomainValidationEmailPrefill = () => {
  const client = useApolloClient();

  return useCallback(
    async (thirdPartyOrganizationId?: string): Promise<boolean> => {
      if (!thirdPartyOrganizationId) {
        return false;
      }

      const { data } = await client.query<ProductFeaturesQueryData, ProductFeaturesQueryVariables>({
        query: productFeaturesQuery,
        variables: {
          input: {
            idType: IdTypes.Organization,
            id: thirdPartyOrganizationId,
            featureIdentifications: [{ namespace: NAMESPACE, name: FEATURE_NAME }],
          },
        },
      });

      const configuration = data?.GetConfiguredFeatures?.featureConfigurations?.find(
        ({ featureIdentification }) =>
          featureIdentification.namespace === NAMESPACE && featureIdentification.name === FEATURE_NAME
      );

      return configuration?.value === true;
    },
    [client]
  );
};

export default useDisableDomainValidationEmailPrefill;
